import React from "react";
import type { MindMapNode } from "./types";
import type { ExpandedMap } from "./types";
import { MindMapNodeComponent } from "./MindMapNode";

interface MindMapTreeProps {
  node: MindMapNode;
  expanded: ExpandedMap;
  onToggle: (id: string) => void;
  depth?: number;
}

const MindMapTree: React.FC<MindMapTreeProps> = ({
  node,
  expanded,
  onToggle,
  depth = 0,
}) => {
  const isExpanded = !!expanded[node.id];
  const hasChildren = !!node.children?.length;

  const topChildren = hasChildren
    ? node.children!.slice(0, Math.ceil(node.children!.length / 2))
    : [];
  const bottomChildren = hasChildren
    ? node.children!.slice(Math.ceil(node.children!.length / 2))
    : [];

  return (
    <li className="flex flex-row items-center mt-3 first:mt-0">
      <div className="flex flex-col items-start">
        <MindMapNodeComponent
          node={node}
          isExpanded={isExpanded}
          onToggle={onToggle}
          depth={depth}
        />
      </div>

      {hasChildren && (
        <div
          className={`flex flex-col items-start transition-all duration-300 ease-in-out ${
            isExpanded
              ? "ml-6 pl-3 max-w-[3000px] opacity-100"
              : "max-w-0 overflow-hidden opacity-0"
          }`}
        >
          {topChildren.length > 0 && (
            <ul className="flex flex-col-reverse">
              {topChildren.map((child) => (
                <MindMapTree
                  key={child.id}
                  node={child}
                  expanded={expanded}
                  onToggle={onToggle}
                  depth={depth + 1}
                />
              ))}
            </ul>
          )}
          {bottomChildren.length > 0 && (
            <ul className="flex flex-col">
              {bottomChildren.map((child) => (
                <MindMapTree
                  key={child.id}
                  node={child}
                  expanded={expanded}
                  onToggle={onToggle}
                  depth={depth + 1}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
};

interface MindMapTreeRootProps {
  root: MindMapNode;
  expanded: ExpandedMap;
  onToggle: (id: string) => void;
}

export const MindMapTreeRoot: React.FC<MindMapTreeRootProps> = ({
  root,
  expanded,
  onToggle,
}) => {
  const isExpanded = !!expanded[root.id];
  const hasChildren = !!root.children?.length;

  const topChildren = hasChildren
    ? root.children!.slice(0, Math.ceil(root.children!.length / 2))
    : [];
  const bottomChildren = hasChildren
    ? root.children!.slice(Math.ceil(root.children!.length / 2))
    : [];

  return (
    <div className="flex flex-row items-center">
      <div className="flex flex-col items-center">
        <MindMapNodeComponent
          node={root}
          isRoot
          isExpanded={isExpanded}
          onToggle={onToggle}
          depth={0}
        />
      </div>

      {hasChildren && (
        <div
          className={`flex flex-col items-start transition-all duration-300 ease-in-out ${
            isExpanded
              ? "ml-6 pl-3 max-w-[3000px] opacity-100"
              : "max-w-0 overflow-hidden opacity-0"
          }`}
        >
          {topChildren.length > 0 && (
            <ul className="flex flex-col-reverse">
              {topChildren.map((child) => (
                <MindMapTree
                  key={child.id}
                  node={child}
                  expanded={expanded}
                  onToggle={onToggle}
                  depth={1}
                />
              ))}
            </ul>
          )}
          {bottomChildren.length > 0 && (
            <ul className="flex flex-col">
              {bottomChildren.map((child) => (
                <MindMapTree
                  key={child.id}
                  node={child}
                  expanded={expanded}
                  onToggle={onToggle}
                  depth={1}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
